import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

interface Modal {
  id: string;
  component: React.ReactNode;
  props?: Record<string, unknown>;
}

interface UseUIReturn {
  // Modals
  activeModals: Modal[];
  showModal: (
    id: string,
    component: React.ReactNode,
    props?: Record<string, unknown>,
  ) => void;
  hideModal: (id: string) => void;
  hideAllModals: () => void;

  // Theme
  isDarkMode: boolean;
  toggleDarkMode: () => void;

  // Documentation Panel
  isDocumentationPanelOpen: boolean;
  documentationPanelUrl?: string;
  toggleDocumentationPanel: () => void;
  openDocumentationPanel: (url?: string) => void;

  // Embed Mode
  isEmbedMode: boolean;
}

export function useUI(): UseUIReturn {
  const [activeModals, setActiveModals] = useState<Modal[]>([]);
  const [searchParams] = useSearchParams();
  const isEmbedMode = searchParams.get('embed') === 'true';
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const themeParam = searchParams.get('theme');
    if (themeParam === 'dark' || themeParam === 'light') {
      return themeParam === 'dark';
    }
    const savedTheme = localStorage.getItem('theme');
    const isDark = savedTheme === 'dark';
    if (isDark) {
      document.documentElement.classList.add('dark');
    }
    return isDark;
  });

  const [isDocumentationPanelOpen, setIsDocumentationPanelOpen] = useState(() => {
    const savedState = localStorage.getItem('isDocumentationPanelOpen');
    return savedState === 'true';
  });

  const [documentationPanelUrl, setDocumentationPanelUrl] = useState<string | undefined>();

  // Update theme when URL parameter changes
  useEffect(() => {
    const themeParam = searchParams.get('theme');
    if (themeParam === 'dark' || themeParam === 'light') {
      const isDark = themeParam === 'dark';
      setIsDarkMode(isDark);
      document.documentElement.classList.toggle('dark', isDark);
    }
  }, [searchParams]);

  // Modal handlers
  const showModal = useCallback(
    (
      id: string,
      component: React.ReactNode,
      props?: Record<string, unknown>,
    ) => {
      setActiveModals((prev) => [...prev, { id, component, props }]);
    },
    [],
  );

  const hideModal = useCallback((id: string) => {
    setActiveModals((prev) => prev.filter((modal) => modal.id !== id));
  }, []);

  const hideAllModals = useCallback(() => {
    setActiveModals([]);
  }, []);

  // Theme handlers
  const toggleDarkMode = useCallback(() => {
    setIsDarkMode((prev) => {
      const newValue = !prev;
      localStorage.setItem('theme', newValue ? 'dark' : 'light');
      document.documentElement.classList.toggle('dark', newValue);
      return newValue;
    });
  }, []);

  const toggleDocumentationPanel = useCallback(() => {
    setIsDocumentationPanelOpen((prev) => {
      const newValue = !prev;
      localStorage.setItem('isDocumentationPanelOpen', String(newValue));
      return newValue;
    });
  }, []);

  const openDocumentationPanel = useCallback((url?: string) => {
    setDocumentationPanelUrl(url);
    setIsDocumentationPanelOpen(true);
    localStorage.setItem('isDocumentationPanelOpen', 'true');
  }, []);

  return {
    activeModals,
    showModal,
    hideModal,
    hideAllModals,
    isDarkMode,
    toggleDarkMode,
    isDocumentationPanelOpen,
    documentationPanelUrl,
    toggleDocumentationPanel,
    openDocumentationPanel,
    isEmbedMode,
  };
}
